import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  BookmarkAdd as BookmarkAddIcon,
  Build as SolutionIcon,
  NotificationsActive as FollowingIcon,
  NotificationsOff as NotFollowingIcon,
  Assignment as AssignmentIcon,
  Event as EventIcon,
  HighlightOff as CancelIcon,
  Construction as BuildIcon
} from '@mui/icons-material';
import { useThemeSettings } from '../../../../contexts/ThemeContext';
import { usePermissions } from '../../../../contexts/PermissionContext';
import StatusChip from '../shared/StatusChip';

/**
 * Ticket detail header with title, status and action buttons
 */
const TicketHeader = ({
  ticket,
  isSupport,
  isFollowing,
  followersCount,
  loadingFollow,
  linkedIntervention,
  linkedInterventions = [],
  onBack,
  onEdit,
  onDelete,
  onSaveAsKnownIssue,
  onApplySolution,
  onToggleFollow,
  onViewReport,
  onReschedule,
  onCancelIntervention,
  onRequestIntervention
}) => {
  const { t } = useThemeSettings();
  const { hasPermission } = usePermissions();

  const canRequestIntervention = isSupport &&
    (!linkedIntervention || linkedIntervention.status === 'cancelled' || linkedIntervention.status === 'completed');

  const canModifyIntervention = isSupport &&
    linkedIntervention &&
    linkedIntervention.status !== 'cancelled' &&
    linkedIntervention.status !== 'completed';

  return (
    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
      {/* Left side - Back button and title */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={onBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          {t ? t("VisualTreeView.ticket") : "Ticket #"}{ticket.id}
        </Typography>
      </Box>

      {/* Right side - Action buttons */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {/* Status chip */}
        <StatusChip status={ticket.status} />

        {/* Follow button */}
        <Tooltip title={isFollowing ? 'Unfollow ticket' : 'Follow ticket'}>
          <Button
            variant={isFollowing ? 'contained' : 'outlined'}
            color={isFollowing ? 'primary' : 'inherit'}
            startIcon={isFollowing ? <FollowingIcon /> : <NotFollowingIcon />}
            onClick={onToggleFollow}
            disabled={loadingFollow}
            size="small"
          >
            {loadingFollow ? 'Loading...' : isFollowing ? 'Following' : 'Follow'}
            {followersCount > 0 && ` (${followersCount})`}
          </Button>
        </Tooltip>

        {/* Intervention buttons */}
        {linkedIntervention && isSupport && (
          <>
            <Tooltip title={linkedInterventions.length > 1 ? "Select an intervention report" : "View Report"}>
              <Button
                variant="contained"
                color="success"
                startIcon={<AssignmentIcon />}
                onClick={onViewReport}
                size="small"
              >
                {t ? t("Tickets.view_intervention") : "View Report"}
                {linkedInterventions.length > 1 && ` (${linkedInterventions.length})`}
              </Button>
            </Tooltip>

            {canModifyIntervention && (
              <>
                <Tooltip title={t ? t("Tickets.reschedule_this_intervention") : "Reschedule"}>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<EventIcon />}
                    onClick={onReschedule}
                    size="small"
                  >
                    {t ? t("Tickets.reschedule") : "Reschedule"}
                  </Button>
                </Tooltip>

                <Tooltip title={t ? t("Tickets.cancel_this_intervention") : "Cancel intervention"}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={onCancelIntervention}
                    size="small"
                  >
                    {t ? t("Common.cancel") : "Cancel"}
                  </Button>
                </Tooltip>
              </>
            )}
          </>
        )}

        {canRequestIntervention && (
          <Tooltip title={t ? t("Tickets.request_physical_intervention_this_t") : "Request intervention"}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<BuildIcon />}
              onClick={onRequestIntervention}
              size="small"
            >
              {t ? t("Tickets.intervention") : "Intervention"}
            </Button>
          </Tooltip>
        )}

        {/* Edit button */}
        {hasPermission('tickets.edit') && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEdit}
            size="small"
          >
            {t ? t("DeviceTable.edit") : "Edit"}
          </Button>
        )}

        {/* Support-only buttons */}
        {isSupport && (
          <>
            <Button
              variant="outlined"
              startIcon={<BookmarkAddIcon />}
              onClick={onSaveAsKnownIssue}
              size="small"
            >
              {t ? t("SaveAsKnownIssue.save_as_known_issue") : "Save as Known Issue"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SolutionIcon />}
              onClick={onApplySolution}
              size="small"
            >
              {t ? t("Tickets.apply_solution") : "Apply Solution"}
            </Button>
          </>
        )}

        {/* Delete button */}
        {hasPermission('tickets.delete') && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            size="small"
          >
            {t ? t("DeviceTable.delete") : "Delete"}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default TicketHeader;
